import React, { useEffect, useState } from "react";

const EvidenceStatement: React.FC = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showBackToTop, setShowBackToTop] = useState(false);

  const handleMobileMenuToggle = () => setMobileMenuOpen((open) => !open);

  useEffect(() => {
    const onScroll = () => {
      setShowBackToTop(window.scrollY > 200);
    };
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleBackToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <>
      {/* Header */}
      <header className="bg-white shadow-md py-4 px-6 md:px-12 fixed w-full z-[1000] top-0">
        <nav className="container mx-auto flex justify-between items-center">
          <a
            href="/"
            className="text-2xl font-bold text-brand-dark-blue rounded-md p-2 hover:bg-gray-100 transition-colors"
          >
            <img
              src="/assets/Amblify_logo_zilsfix.png"
              alt="Amblyfy"
              className="h-10"
            />
          </a>
          <div className="hidden md:flex space-x-6">
            <a
              href="/"
              className="text-gray-600 hover:text-brand-dark-blue font-medium transition-colors rounded-md px-3 py-2"
            >
              Home
            </a>
            <a
              href="/login"
              className="text-white bg-brand-dark-blue hover:bg-brand-cyan font-bold transition-colors rounded-md px-4 py-2 ml-4 shadow-md hover:scale-105 duration-200"
            >
              Log In
            </a>
          </div>
          <button
            className="md:hidden text-gray-600 focus:outline-none focus:ring-2 focus:ring-brand-dark-blue rounded-md p-2"
            onClick={handleMobileMenuToggle}
            aria-label="Toggle mobile menu"
            aria-expanded={mobileMenuOpen}
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M4 6h16M4 12h16M4 18h16"
              ></path>
            </svg>
          </button>
        </nav>
        <div
          className={`md:hidden bg-white py-2 mt-2 border-t border-gray-200${
            mobileMenuOpen ? "" : " hidden"
          }`}
        >
          <a
            href="/"
            onClick={() => setMobileMenuOpen(false)}
            className="block px-4 py-2 text-gray-700 hover:bg-brand-pale-green hover:text-brand-dark-blue transition-colors rounded-md mx-2"
          >
            Home
          </a>
          <a
            href="/login"
            onClick={() => setMobileMenuOpen(false)}
            className="block px-4 py-2 text-white bg-brand-dark-blue hover:bg-brand-cyan font-bold transition-colors rounded-md mx-2 mt-2 shadow-md text-center"
          >
            Log In
          </a>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-20">
        <section className="bg-gradient-to-br from-brand-dark-blue via-brand-cyan to-brand-dark-green text-white py-20 px-6 md:px-12 text-center">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Evidence Statement
            </h1>
            <p className="text-xl opacity-90">
              Patching for Amblyopia in Children
            </p>
          </div>
        </section>

        <section className="py-16 px-6 md:px-12 bg-white">
          <div className="max-w-4xl mx-auto prose prose-lg">
            <div className="space-y-8">
              <div className="bg-gray-50 p-6 mb-8">
                <h2 className="text-2xl font-bold text-brand-dark-blue mb-4">
                  Medical Evidence Summary
                </h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Patching is a well-established, evidence-based treatment for
                  amblyopia in children and is endorsed by major clinical
                  guidelines. The American Academy of Ophthalmology's Amblyopia
                  Preferred Practice Pattern (2023) recommends patching when
                  refractive correction alone is insufficient, with specific
                  dosing regimens based on amblyopia severity (2 hours/day for
                  moderate cases; 6 or more hours/day for severe cases).
                </p>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Randomized controlled trials, including the EuPatch trial
                  (Lancet, 2024), have demonstrated that patching significantly
                  improves visual acuity, with early and intensive regimens
                  achieving higher treatment success rates. Systematic reviews
                  and meta-analyses confirm that patching provides clinically
                  meaningful improvement in visual acuity, especially in
                  children with worse baseline vision, and is modestly superior
                  to alternative therapies such as dichoptic treatment.
                </p>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Long-term follow-up studies show that the benefits of patching
                  are durable through adolescence. Adverse effects are generally
                  mild, most commonly skin irritation, and can be mitigated with
                  alternative patching methods. Combination approaches, such as
                  patching plus pharmacologic penalization (atropine), may
                  further enhance outcomes in select cases.
                </p>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-brand-dark-blue mb-6">
                  Clinical Guidelines and Recommendations
                </h2>
                <div className="space-y-4">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="text-lg font-semibold text-brand-cyan mb-2">
                      American Academy of Ophthalmology (2023)
                    </h3>
                    <p className="text-gray-700">
                      <strong>Recommendation:</strong> Patching when refractive
                      correction alone is insufficient
                    </p>
                    <p className="text-gray-700">
                      <strong>Dosing:</strong> 2 hours/day for moderate cases;
                      6+ hours/day for severe cases
                    </p>
                  </div>

                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="text-lg font-semibold text-brand-cyan mb-2">
                      Treatment Success Rates
                    </h3>
                    <p className="text-gray-700">
                      Early and intensive patching regimens achieve higher
                      treatment success rates compared to delayed or less
                      intensive approaches.
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-brand-dark-blue mb-6">
                  Key Research Findings
                </h2>
                <div className="space-y-4">
                  <div className=" pl-4">
                    <h3 className="text-lg font-semibold text-brand-dark-blue mb-2">
                      EuPatch Trial (Lancet, 2024)
                    </h3>
                    <p className="text-gray-700">
                      Multicenter, randomized controlled trial demonstrating
                      significant improvement in visual acuity with early
                      patching regimens.
                    </p>
                  </div>

                  <div className=" pl-4">
                    <h3 className="text-lg font-semibold text-brand-dark-blue mb-2">
                      Systematic Reviews & Meta-Analyses
                    </h3>
                    <p className="text-gray-700">
                      Confirm clinically meaningful improvement in visual
                      acuity, especially in children with worse baseline vision.
                      Patching is modestly superior to alternative therapies
                      such as dichoptic treatment.
                    </p>
                  </div>

                  <div className=" pl-4">
                    <h3 className="text-lg font-semibold text-brand-dark-blue mb-2">
                      Long-term Follow-up Studies
                    </h3>
                    <p className="text-gray-700">
                      Benefits of patching are durable through adolescence,
                      providing lasting improvements in visual function.
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-brand-dark-blue mb-6">
                  Safety and Adverse Effects
                </h2>
                <div className="bg-gray-50  p-4">
                  <p className="text-gray-700 leading-relaxed">
                    <strong>Safety Profile:</strong> Adverse effects are
                    generally mild, most commonly skin irritation. These can be
                    mitigated with alternative patching methods such as
                    over-glasses patching or different adhesive materials.
                  </p>
                </div>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-brand-dark-blue mb-6">
                  Combination Therapies
                </h2>
                <div className="bg-gray-50 p-4">
                  <p className="text-gray-700 leading-relaxed">
                    <strong>Enhanced Outcomes:</strong> Combination approaches,
                    such as patching plus pharmacologic penalization (atropine),
                    may further enhance outcomes in select cases, particularly
                    for severe amblyopia.
                  </p>
                </div>
              </div>

              <div>
                <h2 className="text-2xl font-bold text-brand-dark-blue mb-6">
                  References
                </h2>
                <div className="bg-gray-50 p-6 rounded-lg">
                  <ol className="list-decimal list-inside space-y-3 text-sm text-gray-700">
                    <li>
                      Cruz OA, Repka MX, Hercinovic A, et al. Amblyopia
                      Preferred Practice Pattern. Ophthalmology.
                      2023;130(3):P136-P178. doi:10.1016/j.ophtha.2022.11.003.
                    </li>
                    <li>
                      Proudlock FA, Hisaund M, Maconachie G, et al. Extended
                      Optical Treatment Versus Early Patching With an Intensive
                      Patching Regimen in Children With Amblyopia in Europe
                      (EuPatch): A Multicentre, Randomised Controlled Trial.
                      Lancet. 2024;403(10438):1766-1778.
                      doi:10.1016/S0140-6736(23)02893-3.
                    </li>
                    <li>
                      Jonas DE, Amick HR, Wallace IF, et al. Vision Screening in
                      Children Aged 6 Months to 5 Years: Evidence Report and
                      Systematic Review for the US Preventive Services Task
                      Force. JAMA. 2017;318(9):845-858.
                      doi:10.1001/jama.2017.9900.
                    </li>
                    <li>
                      Halabi MH, Aldhahwani B. The Efficacy and Safety of
                      Dichoptic Therapy in Comparison to Patching in Children
                      With Amblyopia: A Systematic Review and Meta-Analysis.
                      Acta Ophthalmologica. 2025. doi:10.1111/aos.17583.
                    </li>
                    <li>
                      Nughays RO, Almazroy EA, Elyas SK, et al. Efficacy of
                      Dichoptic Treatment vs Eye Patching in Pediatric Patients
                      With Amblyopia: A Systematic Review and Meta-Analysis of
                      Randomized Controlled Trials. Clin Ophthalmol.
                      2025;19:1999-2009. doi:10.2147/OPTH.S513329.
                    </li>
                    <li>
                      Wang S, Wen W, Zhu W, et al. Effect of Combined Atropine
                      and Patching vs Patching Alone for Treatment of Severe
                      Amblyopia in Children Aged 3 to 12 Years: A Randomized
                      Clinical Trial. JAMA Ophthalmol. 2021;139(9):990-996.
                      doi:10.1001/jamaophthalmol.2021.2413.
                    </li>
                    <li>
                      Xu K, Wang L, Bi C. Prospective Observational Study of
                      Three Real-World Amblyopia Treatments in Children:
                      Clinical Outcomes and Predictive Factors. Graefes Arch
                      Clin Exp Ophthalmol. 2025. doi:10.1007/s00417-025-06913-7.
                    </li>
                    <li>
                      Kim SJ, Jeon H, Jung JH, Lee KM, Choi HY. Comparison
                      Between Over-Glasses Patching and Adhesive Patching for
                      Children With Moderate Amblyopia: A Prospective Randomized
                      Clinical Trial. Graefes Arch Clin Exp Ophthalmol.
                      2018;256(2):429-437. doi:10.1007/s00417-017-3851-2.
                    </li>
                  </ol>
                </div>
              </div>

              <div className="bg-gray-50 p-6 rounded-lg">
                <h2 className="text-xl font-bold text-brand-dark-blue mb-4">
                  Important Disclaimer
                </h2>
                <p className="text-gray-700 leading-relaxed">
                  This evidence statement is provided for informational purposes
                  only and should not be considered as medical advice. Treatment
                  decisions should always be made in consultation with qualified
                  healthcare professionals who can assess individual patient
                  needs and circumstances.
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Back to Top Button */}
      {showBackToTop && (
        <button
          className="fixed bottom-8 right-8 bg-gradient-to-r from-brand-dark-blue to-brand-cyan text-white p-4 rounded-full shadow-2xl hover:shadow-3xl hover:scale-110 focus:outline-none focus:ring-4 focus:ring-brand-cyan/50 transition-all duration-300 transform backdrop-blur-sm"
          onClick={handleBackToTop}
          aria-label="Back to top"
        >
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M5 10l7-7m0 0l7 7m-7-7v18"
            />
          </svg>
        </button>
      )}

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 py-8 w-full">
        <div className="w-full px-4">
          <div className="max-w-7xl mx-auto flex flex-col items-center space-y-4">
            <p className="text-gray-600">
              &copy; 2025 Amblyfy. All rights reserved.
            </p>
            <div className="flex flex-wrap justify-center gap-4 text-sm">
              <a
                href="/privacy-policy"
                className="text-gray-600 hover:text-brand-dark-blue transition-colors"
              >
                Privacy Policy
              </a>
              <a
                href="/terms-of-service"
                className="text-gray-600 hover:text-brand-dark-blue transition-colors"
              >
                Terms of Service
              </a>
              <a
                href="/evidence-statement"
                className="text-brand-dark-blue font-medium"
              >
                Evidence Statement
              </a>
              <a
                href="/activity-tracking-guide"
                className="text-gray-600 hover:text-brand-dark-blue transition-colors"
              >
                Activity Tracking Guide
              </a>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
};

export default EvidenceStatement;
